// utils/dateHelpers.ts
import { format, formatDistance, formatRelative, isValid, parseISO } from "date-fns";

type DateFormatType = 
  | 'full'           // "January 1, 2024"
  | 'long'           // "Jan 1, 2024"
  | 'short'          // "01/01/2024"
  | 'iso'            // "2024-01-01"
  | 'relative'       // "2 days ago"
  | 'datetime'       // "Jan 1, 2024, 3:30 PM"
  | 'time'           // "3:30 PM"
  | 'monthDay'       // "Jan 1"
  | 'yearMonth'      // "Jan 2024"
  | 'api'            // "2024-01-01T00:00:00.000Z"
  | 'compact';       // "1/1/24, 3:30 PM"

type FormatDateOptions = {
  fallback?: string;
  capitalize?: boolean;
};

/**
 * Format a date with various preset formats
 * @param dateString - Date string, timestamp, or Date object
 * @param formatType - Preset format type (default: 'full')
 * @param options - Additional formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string | number | Date | null | undefined,
  formatType: DateFormatType = 'full',
  options: FormatDateOptions = {}
): string => {
  const { fallback = 'N/A', capitalize = false } = options;

  if (!dateString) return fallback;

  try {
    let date: Date;
    
    // Handle different input types
    if (dateString instanceof Date) {
      date = dateString;
    } else if (typeof dateString === 'number') {
      date = new Date(dateString);
    } else {
      date = parseISO(dateString);
    }

    // Check if date is valid
    if (!isValid(date)) return fallback;

    let formatted: string;

    switch (formatType) {
      case 'full':
        formatted = format(date, 'PPPP'); // "Thursday, January 1st, 2024"
        break;
      case 'long':
        formatted = format(date, 'PP'); // "Jan 1, 2024"
        break;
      case 'short':
        formatted = format(date, 'P'); // "01/01/2024"
        break;
      case 'iso':
        formatted = format(date, 'yyyy-MM-dd');
        break;
      case 'relative':
        formatted = formatDistance(date, new Date(), { addSuffix: true });
        break;
      case 'datetime':
        formatted = format(date, 'PPp'); // "Jan 1, 2024, 3:30 PM"
        break;
      case 'time':
        formatted = format(date, 'p'); // "3:30 PM"
        break;
      case 'monthDay':
        formatted = format(date, 'MMM d'); // "Jan 1"
        break;
      case 'yearMonth':
        formatted = format(date, 'MMM yyyy'); // "Jan 2024"
        break;
      case 'api':
        formatted = date.toISOString();
        break;
      case 'compact':
        formatted = format(date, 'Pp'); // "1/1/24, 3:30 PM"
        break;
      default:
        formatted = format(date, 'PP');
    }

    return capitalize ? formatted.charAt(0).toUpperCase() + formatted.slice(1) : formatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
  }
};

// More specific helper functions for common use cases
export const formatDateTime = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'datetime');
};

export const formatRelativeDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return 'N/A';
    
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  } catch {
    return 'N/A';
  }
};

export const formatTimeAgo = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'relative');
};

export const formatShortDate = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'short');
};

export const formatLongDate = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'long');
};

export const formatMonthYear = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'yearMonth');
};

// For forms and inputs
export const formatDateForInput = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'iso');
};

// For displaying durations
export const formatDuration = (startDate: string | Date, endDate: string | Date): string => {
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
    
    if (!isValid(start) || !isValid(end)) return 'Invalid dates';
    
    return formatDistance(start, end);
  } catch {
    return 'N/A';
  }
};

// For displaying age
export const formatAge = (birthDate: string | Date): number | null => {
  try {
    const birth = typeof birthDate === 'string' ? parseISO(birthDate) : birthDate;
    if (!isValid(birth)) return null;
    
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  } catch {
    return null;
  }
};

// For displaying date ranges
export const formatDateRange = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  formatType: DateFormatType = 'short'
): string => {
  const start = formatDate(startDate, formatType);
  const end = formatDate(endDate, formatType);
  
  if (start === 'N/A' && end === 'N/A') return 'N/A';
  if (start === 'N/A') return `Until ${end}`;
  if (end === 'N/A') return `From ${start}`;
  
  return `${start} - ${end}`;
};

// For checking if date is today
export const isToday = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  } catch {
    return false;
  }
};

// For checking if date is in the past
export const isPast = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    
    return dateObj < new Date();
  } catch {
    return false;
  }
};

// For checking if date is in the future
export const isFuture = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    
    return dateObj > new Date();
  } catch {
    return false;
  }
};

import { useLang } from "../context/TranslationProvider";

export default function timeAgo(timestamp: number) {
  const { t } = useLang();
  const now = Date.now();
  const diff = now - timestamp * 1000; // backend gives seconds

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds} ${t("sec_ago")}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${t("min_ago")}`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${t("hrs_ago")}`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${t("days_ago")}`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${t("months_ago")}`;

  const years = Math.floor(months / 12);
  return `${years} ${t("yrs_ago")}`;
}

export function minutesToTime(minutes: number, use12Hour = true): string {
  let hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  let period = "";
  if (use12Hour) {
    period = hrs >= 12 ? " PM" : " AM";
    if (hrs > 12) hrs -= 12;
    if (hrs === 0) hrs = 12;
  }

  const hh = hrs.toString().padStart(2, "0");
  const mm = mins.toString().padStart(2, "0");

  return `${hh}:${mm}${period}`;
}


const DHIVEHI_MONTHS = [
  "ޖެނުއަރީ",
  "ފެބްރުއަރީ",
  "މާރޗް",
  "އޭޕްރިލް",
  "މޭ",
  "ޖޫން",
  "ޖުލައި",
  "އޯގަސްޓް",
  "ސެޕްޓެމްބަރު",
  "އޮކްޓޯބަރު",
  "ނޮވެމްބަރު",
  "ޑިސެމްބަރު",
];

const DHIVEHI_DAYS = [
  "އާދިއްތަ",
  "ހޯމަ",
  "އަންގާރަ",
  "ބުދަ",
  "ބުރާސްފަތި",
  "ހުކުރު",
  "ހޮނިހިރު",
];

type DateInput = Date | string | number;

type DateFormatOptions = {
  weekday?: "long" | "short";
  day?: "numeric" | "2-digit";
  month?: "long" | "short" | "numeric";
  year?: "numeric" | "2-digit";
  withDayName?: boolean;
};

export function toArabicDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);

  return new Intl.DateTimeFormat("ar", {
    weekday: options.weekday ?? "long",
    day: options.day ?? "numeric",
    month: options.month ?? "long",
    year: options.year ?? "numeric",
  }).format(date);
}

export function toArabicHijriDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);

  return date.toLocaleDateString("ar-u-ca-islamic", {
    weekday: options.withDayName === false ? undefined : "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function toArabicGregorianDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);

  return date.toLocaleDateString("ar", {
    weekday: options.withDayName === false ? undefined : "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}


export function toDhivehiDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);

  return new Intl.DateTimeFormat("dv-MV", {
    weekday: options.weekday ?? "long",
    day: options.day ?? "numeric",
    month: options.month ?? "long",
    year: options.year ?? "numeric",
  }).format(date);
}



export function toDhivehiGregorianDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  const dayName = DHIVEHI_DAYS[date.getDay()];
  const day = date.getDate();           // English number
  const month = DHIVEHI_MONTHS[date.getMonth()];
  const year = date.getFullYear();      // English number

  return options.withDayName === false
    ? `${day} ${month} ${year}`
    : `${dayName}، ${day} ${month} ${year}`;
}

export function toDhivehiHijriDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);

  return date.toLocaleDateString("dv-MV-u-ca-islamic", {
    weekday: options.withDayName === false ? undefined : "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}


export function toEnglishDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    weekday: options.withDayName === false ? undefined : (options.weekday ?? "long"),
    day: options.day ?? "numeric",
    month: options.month ?? "long",
    year: options.year ?? "numeric",
  }).format(date);
}

export function toEnglishShortDate(input: DateInput): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function toEnglishHijriDate(
  input: DateInput,
  options: DateFormatOptions = {}
): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-u-ca-islamic", {
    weekday: options.withDayName === false ? undefined : "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function toEnglishDateTime(
  input: DateInput,
  use12Hour = true
): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: use12Hour,
  }).format(date);
}

export function toISODate(input: DateInput): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
}

export function toISOStringFull(input: DateInput): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) return "";

  return date.toISOString();
}


export type DateLocaleType =
  | "dv-gregorian"
  | "dv-hijri"
  | "ar-gregorian"
  | "ar-hijri"
  | "en-gregorian";

export function formatLocalizedDate(
  input: DateInput,
  type: DateLocaleType,
  options?: DateFormatOptions
): string {
  switch (type) {
    case "dv-gregorian":
      return toDhivehiGregorianDate(input, options);

    case "dv-hijri":
      return toDhivehiHijriDate(input, options);

    case "ar-gregorian":
      return toArabicGregorianDate(input, options);

    case "ar-hijri":
      return toArabicHijriDate(input, options);

    case "en-gregorian":
      return toEnglishDate(input, options);

    default:
      return new Date(input).toLocaleDateString();
  }
}